import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaCompass, FaMapMarkedAlt, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";

import ServiceOrderAudioRecorder from "./ServiceOrderAudioRecorder";
import {
  fetchGroupFarms,
  fetchGroupLandPlots,
  landPlotsForFarms,
} from "./serviceOrderLocationApi";
import {
  createTrackHeadingPreset,
  fetchTrackHeadingPresets,
} from "./trackHeadingApi";
import {
  formatDistance,
  formatTrackHeadingSummary,
} from "./rumoPistaUtils";
import TrackHeadingMapModal from "./TrackHeadingMapModal";
import "./ServiceOrderTrackHeadingFields.css";

function formatCoordPair(point) {
  if (!point) return "";
  return `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;
}

function ServiceOrderTrackHeadingFields({
  farmId,
  serviceType,
  selectedPresetId,
  onPresetIdChange,
  onApplyLocation,
  selectedServiceFarmIds,
  selectedLandPlotIds,
  trackHeadingAudioBlob,
  onTrackHeadingAudioChange,
  publishToFarmMap,
  onPublishToFarmMapChange,
  t,
}) {
  const [presets, setPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [draftBearing, setDraftBearing] = useState(null);
  const [presetName, setPresetName] = useState("");
  const [saveServiceFarmId, setSaveServiceFarmId] = useState("");
  const [saveLandPlotIds, setSaveLandPlotIds] = useState([]);
  const [groupFarms, setGroupFarms] = useState([]);
  const [allLandPlots, setAllLandPlots] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);

  const selectedPreset = useMemo(
    () => presets.find((item) => String(item.id) === String(selectedPresetId)),
    [presets, selectedPresetId]
  );

  const visibleSaveLandPlots = useMemo(
    () =>
      saveServiceFarmId
        ? landPlotsForFarms(allLandPlots, [parseInt(saveServiceFarmId, 10)])
        : [],
    [allLandPlots, saveServiceFarmId]
  );

  const loadPresets = useCallback(async () => {
    if (!farmId) return;
    setLoadingPresets(true);
    try {
      const data = await fetchTrackHeadingPresets({
        farmId,
        serviceType: serviceType || undefined,
      });
      setPresets(data);
    } catch (error) {
      setPresets([]);
    } finally {
      setLoadingPresets(false);
    }
  }, [farmId, serviceType]);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  useEffect(() => {
    if (!farmId) return;
    fetchGroupFarms(farmId)
      .then(setGroupFarms)
      .catch(() => setGroupFarms([]));
    fetchGroupLandPlots(farmId)
      .then(setAllLandPlots)
      .catch(() => setAllLandPlots([]));
  }, [farmId]);

  useEffect(() => {
    if (saveServiceFarmId) return;
    const firstSelected = Array.isArray(selectedServiceFarmIds)
      ? selectedServiceFarmIds[0]
      : null;
    if (firstSelected) {
      setSaveServiceFarmId(String(firstSelected));
      return;
    }
    if (farmId) {
      setSaveServiceFarmId(String(farmId));
    }
  }, [farmId, saveServiceFarmId, selectedServiceFarmIds]);

  useEffect(() => {
    if (!saveServiceFarmId) return;
    const validIds = new Set(visibleSaveLandPlots.map((plot) => plot.id));
    setSaveLandPlotIds((current) => {
      const fromParent = Array.isArray(selectedLandPlotIds)
        ? selectedLandPlotIds.filter((id) => validIds.has(id))
        : [];
      if (fromParent.length > 0) return fromParent;
      return current.filter((id) => validIds.has(id));
    });
  }, [saveServiceFarmId, selectedLandPlotIds, visibleSaveLandPlots]);

  const handleSelectPreset = (presetId) => {
    onPresetIdChange(presetId);
    if (!presetId) {
      return;
    }

    const preset = presets.find((item) => String(item.id) === String(presetId));
    if (!preset) return;

    onApplyLocation({
      serviceFarmIds: preset.service_farm ? [preset.service_farm] : [],
      landPlotIds: Array.isArray(preset.land_plot_ids) ? preset.land_plot_ids : [],
    });
  };

  const handleMapConfirm = (bearingData) => {
    setDraftBearing(bearingData);
    setPresetName((current) => current || t("Rumo A-B"));
    setShowMapModal(false);
  };

  const toggleSaveLandPlot = (plotId) => {
    setSaveLandPlotIds((current) =>
      current.includes(plotId)
        ? current.filter((id) => id !== plotId)
        : [...current, plotId]
    );
  };

  const handleSavePreset = async () => {
    if (!draftBearing) return;
    if (!presetName.trim()) {
      toast.error(t("Informe um nome para salvar o rumo."), {
        position: "bottom-right",
      });
      return;
    }
    if (!saveServiceFarmId) {
      toast.error(t("Selecione a fazenda do serviço."), {
        position: "bottom-right",
      });
      return;
    }

    const serviceFarm = groupFarms.find(
      (item) => String(item.id) === String(saveServiceFarmId)
    );
    const summaryText = formatTrackHeadingSummary(
      draftBearing,
      serviceFarm ? serviceFarm.nome_da_fazenda : ""
    );

    setSavingPreset(true);
    try {
      const created = await createTrackHeadingPreset({
        context_farm: parseInt(farmId, 10),
        service_farm: parseInt(saveServiceFarmId, 10),
        name: presetName.trim(),
        service_type: serviceType || "",
        point_a_lat: draftBearing.pointA.lat,
        point_a_lng: draftBearing.pointA.lng,
        point_b_lat: draftBearing.pointB.lat,
        point_b_lng: draftBearing.pointB.lng,
        bearing: draftBearing.bearing,
        distance_m: draftBearing.distance,
        summary_text: summaryText,
        land_plot_ids: saveLandPlotIds,
      });

      toast.success(t("Rumo salvo para reutilizar nas próximas ordens."), {
        position: "bottom-right",
      });
      setDraftBearing(null);
      setPresetName("");
      await loadPresets();
      onPresetIdChange(String(created.id));
      onApplyLocation({
        serviceFarmIds: created.service_farm ? [created.service_farm] : [],
        landPlotIds: Array.isArray(created.land_plot_ids)
          ? created.land_plot_ids
          : [],
      });
    } catch (error) {
      toast.error(t("Não foi possível salvar o rumo."), {
        position: "bottom-right",
      });
    } finally {
      setSavingPreset(false);
    }
  };

  const previewData = selectedPreset
    ? {
        bearing: selectedPreset.bearing,
        distance: selectedPreset.distance_m,
        landPlots: selectedPreset.land_plot_names,
        name: selectedPreset.name,
        pointA: {
          lat: selectedPreset.point_a_lat,
          lng: selectedPreset.point_a_lng,
        },
        pointB: {
          lat: selectedPreset.point_b_lat,
          lng: selectedPreset.point_b_lng,
        },
      }
    : draftBearing
      ? {
          bearing: draftBearing.bearing,
          distance: draftBearing.distance,
          landPlots: "",
          name: t("Rumo no mapa (não salvo)"),
          pointA: draftBearing.pointA,
          pointB: draftBearing.pointB,
        }
      : null;

  return (
    <div className="create-schedule-field service-order-track-heading">
      <label>
        <FaCompass style={{ marginRight: 6 }} />
        {t("Rumo de pista A-B")}
        <span className="service-order-track-heading__optional">
          {" "}
          ({t("opcional")})
        </span>
      </label>
      <p className="create-schedule-hint">
        {t(
          "Defina ou escolha um rumo salvo para orientar o piloto automático. Ao selecionar, fazenda e talhões são preenchidos automaticamente."
        )}
      </p>

      <div className="service-order-track-heading__row">
        <select
          className="form-control"
          value={selectedPresetId || ""}
          onChange={(event) => handleSelectPreset(event.target.value)}
          disabled={loadingPresets}
        >
          <option value="">
            {loadingPresets
              ? t("Carregando rumos...")
              : t("Nenhum rumo selecionado")}
          </option>
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.name}
              {preset.land_plot_names ? ` — ${preset.land_plot_names}` : ""}
              {preset.bearing != null ? ` (${preset.bearing.toFixed(1)}°)` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-default service-order-track-heading__map-btn"
          onClick={() => setShowMapModal(true)}
        >
          <FaMapMarkedAlt style={{ marginRight: 6 }} />
          {t("Definir no mapa")}
        </button>
      </div>

      {previewData ? (
        <div className="service-order-track-heading__preview">
          <strong>{previewData.name}</strong>
          <span>
            {t("Rumo")}: {previewData.bearing.toFixed(2)}° ·{" "}
            {formatDistance(previewData.distance)}
          </span>
          {previewData.landPlots ? (
            <span>
              {t("Talhões")}: {previewData.landPlots}
            </span>
          ) : null}
          <div className="service-order-track-heading__coords">
            <div>
              {t("Ponto A")}: {formatCoordPair(previewData.pointA)}
            </div>
            <div>
              {t("Ponto B")}: {formatCoordPair(previewData.pointB)}
            </div>
          </div>
        </div>
      ) : null}

      {draftBearing ? (
        <div className="service-order-track-heading__save-panel">
          <p>{t("Salvar este rumo para reutilizar?")}</p>
          <input
            type="text"
            className="form-control"
            value={presetName}
            onChange={(event) => setPresetName(event.target.value)}
            placeholder={t("Nome do rumo (ex.: Talhão 12 — linha norte)")}
          />
          <select
            className="form-control"
            value={saveServiceFarmId}
            onChange={(event) => {
              setSaveServiceFarmId(event.target.value);
              setSaveLandPlotIds([]);
            }}
          >
            <option value="">{t("Fazenda do serviço")}</option>
            {groupFarms.map((farm) => (
              <option key={farm.id} value={farm.id}>
                {farm.nome_da_fazenda}
              </option>
            ))}
          </select>

          {saveServiceFarmId ? (
            <div>
              <label>{t("Talhões deste rumo")}</label>
              <div className="service-order-track-heading__plots">
                {visibleSaveLandPlots.length === 0 ? (
                  <p className="create-schedule-hint">
                    {t("Nenhum talhão cadastrado nesta fazenda.")}
                  </p>
                ) : (
                  visibleSaveLandPlots.map((plot) => (
                    <label
                      key={plot.id}
                      className="service-order-track-heading__plot-item"
                    >
                      <input
                        type="checkbox"
                        checked={saveLandPlotIds.includes(plot.id)}
                        onChange={() => toggleSaveLandPlot(plot.id)}
                      />
                      <span>
                        {plot.name}
                        {plot.area_h2 ? ` — ${plot.area_h2} ha` : ""}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          ) : null}

          <div className="service-order-track-heading__save-actions">
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSavePreset}
              disabled={savingPreset}
            >
              <FaSave style={{ marginRight: 6 }} />
              {savingPreset ? t("Salvando...") : t("Salvar rumo")}
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={() => setDraftBearing(null)}
              disabled={savingPreset}
            >
              {t("Descartar")}
            </button>
          </div>
        </div>
      ) : null}

      {(selectedPresetId || draftBearing) && onTrackHeadingAudioChange ? (
        <ServiceOrderAudioRecorder
          audioBlob={trackHeadingAudioBlob}
          onAudioChange={onTrackHeadingAudioChange}
          label={t("Áudio do rumo A-B")}
          hint={t(
            "Grave instruções específicas sobre o rumo de pista. O operador ouve ao abrir os detalhes no app."
          )}
          className="service-order-track-heading-audio"
          t={t}
        />
      ) : null}

      {(selectedPresetId || draftBearing) && onPublishToFarmMapChange ? (
        <label className="service-order-track-heading__farm-map-option">
          <input
            type="checkbox"
            checked={Boolean(publishToFarmMap)}
            onChange={(event) => onPublishToFarmMapChange(event.target.checked)}
          />
          <span>
            {t(
              "Enviar também para o mapa da fazenda no app (recomendação A-B visível no mapa)"
            )}
          </span>
        </label>
      ) : null}

      {selectedPresetId || draftBearing ? (
        <button
          type="button"
          className="btn btn-link service-order-track-heading__clear-btn"
          onClick={() => {
            onPresetIdChange("");
            setDraftBearing(null);
            if (onTrackHeadingAudioChange) onTrackHeadingAudioChange(null);
          }}
        >
          {t("Remover rumo desta ordem")}
        </button>
      ) : null}

      {showMapModal ? (
        <TrackHeadingMapModal
          open
          farmId={farmId}
          onClose={() => setShowMapModal(false)}
          onConfirm={handleMapConfirm}
          t={t}
        />
      ) : null}
    </div>
  );
}

export default ServiceOrderTrackHeadingFields;
